import {imageBoxSx} from '@hconnect/common/styles'
import {datetimeFormatter} from '@hconnect/uikit'
import {customThemeConstants, MarkdownText} from '@hconnect/uikit/src/lib2'
import {DetailsCard, CardHeader, Button, LabelRegular, TextRegular, Icon} from '@hm/fundamental'
import {mdiClose, mdiChevronDown, mdiChevronLeft, mdiChevronUp, mdiClockOutline} from '@mdi/js'
import {Box, Link} from '@mui/material'
import {AttachmentItem, AttachmentItemsDisplay} from '@widgets/common-attachments'
import moment from 'moment'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useLayoutStyles} from '../styles'
import {JobsToBeDoneInsightDetails} from '../types'

import {JtbdInsightActionsMenu} from './JtbdInsightActionsMenu'

interface JtbdInsightDetailViewProps {
  insight: JobsToBeDoneInsightDetails
  timezone: string
  onBack: () => void
  onClose: () => void
  onEdit: () => void
  isEditAllowed: boolean
}

export const JtbdInsightDetailView: React.FC<JtbdInsightDetailViewProps> = ({
  insight,
  timezone,
  onBack,
  onClose,
  onEdit,
  isEditAllowed
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {classes} = useLayoutStyles()
  const [activeAttachmentId, setActiveAttachmentId] = useState<string | undefined>(undefined)
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false)

  const attachments = insight.attachments ?? []

  const createdAtFormatted = datetimeFormatter(moment(insight.createdAt), timezone)
  const editedAtFormatted = insight.lastUpdatedAt
    ? datetimeFormatter(moment(insight.lastUpdatedAt), timezone)
    : null

  return (
    <DetailsCard>
      <CardHeader
        containerClassname={classes.sideCardHeaderCompact}
        aside={
          <Box display="flex" alignItems="center" gap={1}>
            {isEditAllowed && <JtbdInsightActionsMenu onEdit={onEdit} />}
            <Button onClick={onClose} variant="tertiary" data-test-id="insight-detail-close">
              <Icon
                path={mdiClose}
                size="regular"
                color={customThemeConstants().palette.barChartDefault}
              />
            </Button>
          </Box>
        }
      >
        <Link
          component="button"
          onClick={onBack}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'primary.main',
            textDecoration: 'none',
            fontSize: '14px',
            cursor: 'pointer'
          }}
          data-test-id="insight-detail-back"
        >
          <Icon path={mdiChevronLeft} size="regular" />
          {t('jobsToBeDone.details.backToActivity')}
        </Link>
      </CardHeader>

      <Box sx={{px: 2, pb: 2}}>
        <Box sx={{mb: 2}}>
          <TextRegular style={{fontWeight: 600, display: 'block'}}>
            {insight.createdBy.name}
          </TextRegular>
          <LabelRegular className="text-foreground_faded" style={{display: 'block'}}>
            {createdAtFormatted}
          </LabelRegular>

          {editedAtFormatted && insight.lastUpdatedBy && (
            <LabelRegular
              className="text-foreground_faded"
              style={{display: 'block', marginTop: '4px'}}
            >
              {t('jobsToBeDone.details.edited')} {editedAtFormatted} {t('jobsToBeDone.table.by')}{' '}
              {insight.lastUpdatedBy.name}
            </LabelRegular>
          )}
        </Box>

        <Box sx={{mb: 2}}>
          <LabelRegular
            className="text-foreground_faded"
            style={{display: 'block', marginBottom: '4px'}}
          >
            {t('jobsToBeDone.details.description')}
          </LabelRegular>
          <MarkdownText>{insight.description}</MarkdownText>
        </Box>

        {insight.timeSpent && (
          <Box sx={{mb: 2}}>
            <LabelRegular
              className="text-foreground_faded"
              style={{display: 'block', marginBottom: '4px'}}
            >
              {t('jobsToBeDone.details.timeSpent')}
            </LabelRegular>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <Icon path={mdiClockOutline} size="regular" />
              <TextRegular>{insight.timeSpent}</TextRegular>
            </Box>
          </Box>
        )}

        {attachments.length > 0 && (
          <Box data-test-id="insight-detail-attachments">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                mb: 1
              }}
              onClick={() => setAttachmentsExpanded((prev) => !prev)}
              data-test-id="insight-detail-attachments-toggle"
            >
              <Icon path={attachmentsExpanded ? mdiChevronUp : mdiChevronDown} size="regular" />
              <TextRegular style={{fontWeight: 600}}>
                {t('jobsToBeDone.details.attachments')} ({attachments.length})
              </TextRegular>
            </Box>
            {attachmentsExpanded && (
              <>
                <Box component="span" sx={imageBoxSx()}>
                  {attachments.map((attachment) => (
                    <AttachmentItem
                      key={attachment.id}
                      attachment={attachment}
                      setActiveAttachmentId={setActiveAttachmentId}
                    />
                  ))}
                </Box>
                {activeAttachmentId !== undefined && (
                  <AttachmentItemsDisplay
                    attachments={attachments}
                    activeAttachmentId={activeAttachmentId}
                    onClose={() => setActiveAttachmentId(undefined)}
                  />
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </DetailsCard>
  )
}
